<template>
  <div style="display: flex; flex-direction: column; gap: 24px;">
    <Breadcrumbs :breadcrumbs="breadcrumbs" class="mt-4"/>
    <div style="display: flex; gap: 24px;">
      <ActivitiesFilters class="on-page"/>
      <div class="results-listing-wrapper">
        <div class="results-listing-header">
          <h1>
            {{searchStore.selectedCity?.name}}<span v-if="total > 0">: {{ total }} {{$t('activities found')}}</span>
            <span v-if="searchStore.searchInProgress" class="loading-indicator">
              <span class="loading-dots">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </span>
            </span>
          </h1>
          <Sorting class="show-desktop" />

          <button @click="showMobileFiltersDialog = true" class="btn btn-secondary btn-size-l show-not-desktop">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.7083 9.99999H7.41246M3.77829 9.99999H2.29163M3.77829 9.99999C3.77829 9.51818 3.96969 9.0561 4.31038 8.71541C4.65107 8.37472 5.11315 8.18332 5.59496 8.18332C6.07677 8.18332 6.53885 8.37472 6.87954 8.71541C7.22023 9.0561 7.41163 9.51818 7.41163 9.99999C7.41163 10.4818 7.22023 10.9439 6.87954 11.2846C6.53885 11.6253 6.07677 11.8167 5.59496 11.8167C5.11315 11.8167 4.65107 11.6253 4.31038 11.2846C3.96969 10.9439 3.77829 10.4818 3.77829 9.99999ZM17.7083 15.5058H12.9183M12.9183 15.5058C12.9183 15.9877 12.7264 16.4503 12.3857 16.7911C12.0449 17.1319 11.5827 17.3233 11.1008 17.3233C10.619 17.3233 10.1569 17.1311 9.81622 16.7904C9.47552 16.4497 9.28413 15.9876 9.28413 15.5058M12.9183 15.5058C12.9183 15.0239 12.7264 14.5621 12.3857 14.2214C12.0449 13.8806 11.5827 13.6892 11.1008 13.6892C10.619 13.6892 10.1569 13.8806 9.81622 14.2212C9.47552 14.5619 9.28413 15.024 9.28413 15.5058M9.28413 15.5058H2.29163M17.7083 4.49416H15.1208M11.4866 4.49416H2.29163M11.4866 4.49416C11.4866 4.01235 11.678 3.55027 12.0187 3.20958C12.3594 2.86889 12.8215 2.67749 13.3033 2.67749C13.5419 2.67749 13.7781 2.72448 13.9985 2.81578C14.2189 2.90707 14.4192 3.04089 14.5879 3.20958C14.7566 3.37827 14.8904 3.57854 14.9817 3.79895C15.073 4.01936 15.12 4.25559 15.12 4.49416C15.12 4.73272 15.073 4.96896 14.9817 5.18937C14.8904 5.40977 14.7566 5.61004 14.5879 5.77873C14.4192 5.94743 14.2189 6.08124 13.9985 6.17254C13.7781 6.26383 13.5419 6.31082 13.3033 6.31082C12.8215 6.31082 12.3594 6.11943 12.0187 5.77873C11.678 5.43804 11.4866 4.97597 11.4866 4.49416Z" stroke="#1985F9" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
            </svg>
            {{$t('Filters')}}
            <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
          </button>
          <DialogCloseOutside
              v-model="showMobileFiltersDialog"
              :showHeader="false"
              position="right"
              :block-scroll="true"
              :modal="true"
              :draggable="false"
              class="modal-container modal-container--filters custom-scrollbar"
          >
            <ActivitiesFiltersMobile class="in-dialog" @close="showMobileFiltersDialog = false"/>
          </DialogCloseOutside>

        </div>

        <ActivitiesList />

        <div v-if="hasMorePages" class="load-more-container">
          <button
            class="btn btn-secondary btn-load-more"
            :disabled="searchStore.loadingMoreActivities"
            @click="loadMore"
          >
            <template v-if="searchStore.loadingMoreActivities">
              {{$t('Loading...')}}
            </template>
            <template v-else>
              {{$t('Load more results')}}
            </template>
          </button>
          <div class="load-more-count">
            {{$t('Showing')}} {{ shownCount }} {{$t('of')}} {{ total }} {{$t('results')}}
          </div>
        </div>

        <div v-if="paginationLength > 1" class="pagination-seo" aria-hidden="true">
          <PaginationClassic
            :value="currentPage"
            :length="paginationLength"
            :link="cityUrl"
            @change="changePage"
          />
        </div>
      </div>
    </div>

    <!-- Below-the-fold SEO content -->
    <div class="seo-content-section">
      <ExperienceCityInfoSection :city-slug="(route.params.city as string)" :info="cityInfo" />
      <NearbyCities :city-slug="(route.params.city as string)" type="experiences" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {useSearchStore} from "~/store/search";
import PaginationClassic from "~/components/PaginationClassic.vue";
import Sorting from "~/components/MarketPage/Sorting.vue";
import DialogCloseOutside from "~/components/DialogCloseOutside.vue";
import ActivitiesList from "~/components/MarketPage/ActivitiesList.vue";
import ActivitiesFilters from "~/components/MarketPage/ActivitiesFilters/ActivitiesFilters.vue";
import ActivitiesFiltersMobile from "~/components/MarketPage/ActivitiesFilters/ActivitiesFiltersMobile.vue";
import ExperienceCityInfoSection from "~/components/ExperienceCityInfoSection.vue";
import type { ExperienceCityInfo } from "~/components/ExperienceCityInfoSection.vue";
import {useRouter} from "vue-router";
import { useHreflangTags, useBreadcrumbSchema, useFaqSchema } from '~/composables/useSeo'
import { imageUrlWithProxy } from '~/utils/imageproxy'

const searchStore = useSearchStore();
const selectedCity = computed(() => searchStore.selectedCity)
const runtimeConfig = useRuntimeConfig()
const route = useRoute()
const router = useRouter()

// This is the feed's landing_page_list_view target, so Google resolves ?currency= here too.
useLandingCurrency()

// --- SSR: Fetch city info for SEO content (Step 2) ---
const baseUrl = runtimeConfig.public.baseURL
const citySlug = route.params.city as string
const { locale } = useI18n()

const { data: _cityInfo } = await useAsyncData(
  `experience-city-info-${citySlug}-${locale.value}`,
  () => $fetch<ExperienceCityInfo>(`${baseUrl}/api/product/v1/experiences/city-info/${citySlug}`, {
    headers: { 'Accept-Language': locale.value }
  }).catch(() => null)
)
const cityInfo = computed(() => _cityInfo.value ?? null)

const showMobileFiltersDialog = ref(false)
const breadcrumbs = computed(() => {
  const items: Array<{label: string, url?: string}> = [
    { label: 'Home', url: '/' },
  ]

  const countryName = cityInfo.value?.country_name || searchStore.selectedCity?.country_name
  const countryCode = cityInfo.value?.country_code || searchStore.selectedCity?.country_code
  if (countryName && countryCode) {
    items.push({ label: countryName, url: `/experiences/destinations/${countryCode.toLowerCase()}` })
  }

  items.push({ label: 'Experiences', url: '/experiences' })
  items.push({ label: cityInfo.value?.city_name || searchStore.selectedCity?.name || 'City' })
  return items
})

const cityUrl = `/experiences/${route.params.city}`

const ACTIVE_FILTER_KEYS = ['price_gte', 'price_lte', 'rating_gte', 'categories']
const activeFilterCount = computed(() => {
  return ACTIVE_FILTER_KEYS.filter(key => {
    const value = route.query[key as keyof typeof route.query]
    return Array.isArray(value) ? value.length > 0 : Boolean(value)
  }).length
})

const total = computed(() => searchStore.totalActivities || 0)
const currentPage = computed(() => searchStore.activityPage || 0)
const productsLimit = 20
const paginationLength = computed(() => {
  return Math.ceil(total.value / productsLimit)
})

const shownCount = computed(() => {
  return searchStore.activitiesProducts?.length || 0
})

const hasMorePages = computed(() => {
  return total.value > 0 && currentPage.value < paginationLength.value
})

function loadMore() {
  const nextPage = (currentPage.value || 1) + 1
  searchStore.loadingMoreActivities = true
  searchStore.searchActivities({ ...route.query, page: nextPage, append: true })
}

const changePage = (page: number) => {
  router.push({
    query: {
      ...route.query,
      page: page
    },
  })
}
//onMounted
onMounted(async () => {
  if (!searchStore.searchPending) {

    if (!searchStore.selectedCity) {
      searchStore.getCity(route.params.city as string).then(() => {
        searchStore.searchActivities(route.query)
      })
    } else {
      await searchStore.searchActivities(route.query)
    }
  }

  await searchStore.getActivityCategories()
})

// Watch for route params changes (city change via client-side navigation)
watch(() => route.params.city, async (newCity, oldCity) => {
  if (newCity && newCity !== oldCity && oldCity) {
    searchStore.clearActivityResults()
    await searchStore.getCity(newCity as string)
    await searchStore.searchActivities(route.query)
  }
})


definePageMeta({
  layout: 'search'
})

// --- SEO Meta Tags (Step 5: Data-driven) ---
const { $siteConfig } = useNuxtApp()
const brand = computed(() => $siteConfig?.value?.brand || 'Tripening')
const cityName = computed(() => cityInfo.value?.city_name || selectedCity.value?.name || '')
const activityCount = computed(() => cityInfo.value?.activity_count || total.value || 0)

const pageTitle = computed(() => {
  if (!cityName.value) return `Discover Experiences & Activities | ${brand.value}`
  const countStr = activityCount.value > 0 ? `${activityCount.value} ` : ''
  return `${countStr}Best Things to Do in ${cityName.value} (${new Date().getFullYear()}) | ${brand.value}`
})

const pageDescription = computed(() => {
  if (!cityName.value) return `Discover amazing tours, activities, and experiences worldwide. Book with ${brand.value} for the best prices.`

  // Prefer AI-generated meta description
  if (cityInfo.value?.ai_meta_description) return cityInfo.value.ai_meta_description

  const c = cityInfo.value
  let desc = `Discover ${activityCount.value > 0 ? activityCount.value + ' ' : ''}tours & activities in ${cityName.value}`
  if (c?.price_min) {
    desc += ` from US$${Math.round(c.price_min)}`
  }
  desc += '.'
  if (c?.top_categories?.length) {
    const catNames = c.top_categories.slice(0, 3).map((cat: any) => cat.name)
    desc += ` Categories: ${catNames.join(', ')}.`
  }
  if (c?.free_cancellation_pct && c.free_cancellation_pct > 50) {
    desc += ` Free cancellation available.`
  }
  desc += ` Book on ${brand.value}.`
  return desc
})

const canonicalUrl = computed(() => {
  const slug = selectedCity.value?.slug || route.params.city || 'results'
  return `${runtimeConfig.public.baseURL}/experiences/${slug}`
})

useHreflangTags()

// --- Structured Data (Step 3) ---

// Breadcrumb JSON-LD
useJsonld(() => useBreadcrumbSchema(breadcrumbs.value, runtimeConfig.public.baseURL))

// ItemList JSON-LD for activity listings
const ssrCitySlug = computed(() => selectedCity.value?.slug || route.params.city || 'results')

useJsonld(() => {
  const activities = searchStore.activitiesProducts || []
  if (!activities.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': `Things to Do in ${cityName.value}`,
    'numberOfItems': total.value,
    'itemListElement': activities.slice(0, 20).map((activity: any, index: number) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        '@type': 'TouristAttraction',
        'name': activity.normalized_name || activity.name,
        'url': `${baseUrl}/experiences/${ssrCitySlug.value}/${activity.id}-${activity.slug}`,
        ...(activity.avg_rating && {
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': activity.avg_rating,
            'bestRating': 5,
          }
        }),
        ...(activity.price_from_with_currency && {
          'offers': {
            '@type': 'Offer',
            'price': activity.price_from_with_currency.amount || activity.price_from_with_currency,
            'priceCurrency': 'USD',
          }
        }),
      }
    }))
  }
})

// TouristDestination JSON-LD
useJsonld(() => {
  if (!cityInfo.value) return null
  const c = cityInfo.value

  return {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    'name': c.city_name,
    'description': pageDescription.value,
    ...(c.latitude && c.longitude && {
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': c.latitude,
        'longitude': c.longitude,
      }
    }),
    ...(c.top_categories?.length && {
      'touristType': c.top_categories.map((cat: any) => cat.name)
    }),
  }
})

// FAQ JSON-LD — prefer AI FAQ when available
const faqItemsForSchema = computed(() => {
  if (!cityInfo.value) return []
  const c = cityInfo.value

  // Prefer AI-generated FAQ
  if (c.ai_faq?.length) {
    return c.ai_faq.map((item: any) => ({ title: item.question, text: item.answer }))
  }

  // Fallback to template-based FAQ
  const items: Array<{ title: string; text: string }> = []

  if (c.top_activities?.length) {
    const topNames = c.top_activities.slice(0, 3).map((a: any) => a.name).join(', ')
    items.push({
      title: `What are the best things to do in ${c.city_name}?`,
      text: `Top-rated experiences in ${c.city_name} include ${topNames}.`,
    })
  }
  if (c.price_min && c.price_max) {
    items.push({
      title: `How much do activities cost in ${c.city_name}?`,
      text: `Activities in ${c.city_name} range from US$${Math.round(c.price_min)} to US$${Math.round(c.price_max)} per person.`,
    })
  }
  if (c.top_categories?.length) {
    const catList = c.top_categories.slice(0, 5).map((cat: any) => `${cat.name} (${cat.count})`).join(', ')
    items.push({
      title: `What are the most popular types of experiences in ${c.city_name}?`,
      text: `The most popular categories are ${catList}.`,
    })
  }
  if (c.free_cancellation_pct > 0) {
    items.push({
      title: `Can I get free cancellation on tours in ${c.city_name}?`,
      text: `Yes, ${c.free_cancellation_pct}% of activities in ${c.city_name} offer free cancellation.`,
    })
  }
  items.push({
    title: `How many tours and activities are available in ${c.city_name}?`,
    text: `There are ${c.activity_count} tours, activities and experiences available in ${c.city_name}.`,
  })
  return items
})

useJsonld(() => useFaqSchema(faqItemsForSchema.value))

// Add noindex for filtered/paginated pages to prevent duplicate content
const activityFilterParams = ['sort', 'price_gte', 'price_lte', 'rating_gte', 'categories']
const hasActivityFilters = computed(() => {
  const pageNum = Number(route.query.page) || 1
  const hasFilterParams = activityFilterParams.some(param => route.query[param])
  return hasFilterParams || pageNum > 1
})
useHead({
  meta: computed(() => hasActivityFilters.value ? [{ name: 'robots', content: 'noindex, follow' }] : [])
})

// og:image fallback: try city image, then first activity cover
const ogImage = computed(() => {
  const cityImgUrl = `${runtimeConfig.public.baseURL}/images/cities/${selectedCity.value?.slug || 'default'}.jpg`
  if (cityInfo.value?.top_activities?.[0]?.cover_image_url) {
    return imageUrlWithProxy(cityInfo.value.top_activities[0].cover_image_url, 1200, 630, 'fill')
  }
  return cityImgUrl
})

// Use useHead with computed values for SSR
useHead({
  title: pageTitle,
  link: [
    { rel: 'canonical', href: canonicalUrl }
  ],
  meta: [
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: ogImage },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: brand },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: pageTitle },
    { name: 'twitter:description', content: pageDescription }
  ]
})
</script>

<style>
.results-listing-wrapper {
  width: 100%;
}

.results-listing-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }

  h1 {
    color: var(--black);
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.loading-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  opacity: 0.6;
  animation: fadeIn 0.5s ease-in;
}

.loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--blue);
  opacity: 0.3;
  animation: pulse 1.4s infinite ease-in-out;
}

.loading-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  40% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.show-desktop {
  @media screen and (max-width: 1024px) {
    display: none !important;
  }
}

.show-not-desktop {
  display: none;
  @media screen and (max-width: 1024px) {
    display: flex !important;
    align-items: center;
    gap: 8px;
  }
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--blue);
  color: var(--white);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 11px;
  font-weight: 600;
}

.load-more-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
}

.btn-load-more {
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
}

.load-more-count {
  font-size: 14px;
  color: var(--dark-grey);
}

.pagination-seo {
  /* Keep pagination for SEO but hide visually since we use load more */
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.seo-content-section {
  padding: 0 0 40px;
}
</style>
